<script setup lang="ts">
import { useEventBus } from '@/composables/useEventBus';
const eventBus = useEventBus();
// import GenerationInfos from '@/components/Admin/GenerationInfos.vue';
import GenerationResultCard from '@/components/Menus/GenerationResultCard.vue';
import TitleSuggestionButton from '@/components/Menus/TitleSuggestionButton.vue';
import AddDishTitleToPlanningDialog from '@/components/Planning/AddDishTitleToPlanningDialog.vue';
import LoadingBar from '@/components/Skeletons/LoadingBar.vue';
import { useFetch } from '@/composables/useFetch';
import { useOnboarding } from '@/composables/useOnboarding';
import type { MessageSchema } from '@/i18n';
import { findMockDishById, getMockDishIdByTitle } from '@/onboarding/mockData';
import { useDishesStore } from '@/stores/dishesStore';
import { useEnvStore } from '@/stores/envStore';
import { usePlanningStore } from '@/stores/planningStore';
import { useUserStore } from '@/stores/useUserStore';
import type { Dish } from '@/types/dish';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import type { Ref } from 'vue';
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const router = useRouter();
const onboarding = useOnboarding();
const dishesStore = ref(useDishesStore());
const titlesToShow = ref(5);
const generatingDish = ref(false);
const { fetchData } = useFetch();
const generationParams = ref(useDishesStore().generationParams);
const lastGenerationParams = ref(useDishesStore().lastGenerationParams);
const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const toast = useToast();
const confirm = useConfirm();
const showAddToPlanningDialog: Ref<boolean> = ref(false);
const selectedDishTitle: Ref<string> = ref('');
const planningStore = usePlanningStore();

const emit = defineEmits<{
  (e: 'selectDish', dish: Dish): void;
  (e: 'selectTitle', title: string): void;
}>();

// if (storedDishes.length === 0 && dishTitles.length === 0) {
//   router.push({ path: `/menu/create` })
// }

// console.log('dishes store from result : ', storedDishes)

function dishClicked(dish: Dish) {
  if (planningStore.choosingDishToPlan) {
    emit('selectDish', dish);
  } else {
    useDishesStore().selectedDish = dish;

    // Si c'est un plat d'onboarding (ID négatif ou >= 90000), sauvegarder l'ID et naviguer normalement
    if (onboarding.isOnboardingActive.value && dish.id < 0) {
      console.log('[Onboarding] Clic sur plat mock, navigation vers ID:', dish.id);
      // Sauvegarder l'ID du plat choisi pour l'afficher dans l'historique
      localStorage.setItem('onboarding:generatedDishId', dish.id.toString());
      // Garder l'ancien nom pour compatibilité
      localStorage.setItem('onboarding_mock_dish_id', dish.id.toString());
    }

    // Naviguer vers la page du plat avec l'ID réel (même pour les IDs négatifs)
    router.push({ path: `/menu/dishes/${dish.id}` });
  }
}

function showMore() {
  const increment = 5;
  if (titlesToShow.value + increment > dishesStore.value.dishTitles.length) {
    titlesToShow.value += dishesStore.value.dishTitles.length - titlesToShow.value; // Only add the remainder if increment exceeds the list length
  } else {
    titlesToShow.value += increment; // Normal case: add 5
  }
}

// function showLess() {
//   const decrement = 5;
//   if (titlesToShow.value - decrement < 5) {
//     titlesToShow.value = 5; // Reset to initial count if decrement goes below 5
//   } else {
//     titlesToShow.value -= decrement; // Normal case: subtract 5
//   }
// }

async function generateDishFromTitle(
  dishTitle: string,
  index: number,
  sourceArray: 'dishTitles' | 'otherDishTitles' | 'otherDishTitlesFromIngredients',
) {
  if (onboarding.isOnboardingActive.value) {
    console.log(`[Onboarding] Appel intercepté. Recherche du mock pour: ${dishTitle}`);
    generatingDish.value = true;

    // 1. Trouver le bon plat mock basé sur le titre -> ID
    const mockDishId = getMockDishIdByTitle(dishTitle);
    if (!mockDishId) {
      console.error(`[Onboarding] Mock dish ID non trouvé pour le titre: ${dishTitle}`);
      generatingDish.value = false;
      return;
    }

    const foundMockDish = findMockDishById(mockDishId);
    if (!foundMockDish) {
      console.error(`[Onboarding] Mock dish non trouvé pour l'ID: ${mockDishId}`);
      generatingDish.value = false;
      return;
    }

    // 2. Stocker l'ID du mock en localStorage pour la page de démo
    localStorage.setItem('onboarding_mock_dish_id', foundMockDish.id.toString());

    // 3. Créer le faux plat (en mode chargement) pour l'affichage visuel
    const placeholderDish: Dish = {
      ...foundMockDish,
      id: Date.now(), // ID temporaire unique
      loading: true,
    };
    dishesStore.value.dishes.push(placeholderDish);

    if (sourceArray === 'dishTitles') {
      dishesStore.value.dishTitles.splice(index, 1);
    }
    // Remplace la liste des résumés par SEULEMENT le nouveau
    dishesStore.value.dishes = [placeholderDish];

    // 4. Simule la "génération" (1 seconde)
    setTimeout(() => {
      const newDishIndex = dishesStore.value.dishes.findIndex((d) => d.id === placeholderDish.id);
      if (newDishIndex !== -1) {
        // Remplace le loader par le vrai faux plat
        const finalMockDish = { ...foundMockDish, id: foundMockDish.id, loading: false };
        dishesStore.value.dishes[newDishIndex] = finalMockDish;

        useDishesStore().selectedDish = finalMockDish;
      }

      // 5. Émettre les événements pour Shepherd
      eventBus.emit('onboarding:recipe-suggestion-clicked');
      setTimeout(() => {
        eventBus.emit('onboarding:recipe-summary-ready');
        // Ne pas rediriger automatiquement, laisser l'utilisateur cliquer
      }, 500);
    }, 1000);
    return; // Stoppe l'appel IA réel
  }
  if (!useUserStore().validateUserCanGenerate()) {
    return null;
  }
  generatingDish.value = true;

  const tempId = Date.now();
  const placeholderDish: Dish = {
    id: tempId,
    name: dishTitle,
    ingredients: [],
    servings: 0,
    difficulty: 1,
    hasRecipe: false,
    loading: true,
    restaurant_id: null,
    author: null,
    created_at: '',
    updated_at: '',
    ai_request_id: 0,
    prep_time_min: 0,
    created_using: 'ia',
    chill_time_min: 0,
    cook_time_min: 0,
    is_favorite: false,
    selected_image: 'gastro',
    is_template: false,
    country_alpha2_code: 'CH',
  };

  dishesStore.value.dishes.push(placeholderDish);

  nextTick(() => {
    const cardElement = document.getElementById(`${tempId}`);
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + rect.top - 75,
        behavior: 'smooth',
      });
    }
  });

  if (sourceArray === 'dishTitles') {
    dishesStore.value.dishTitles.splice(index, 1);
  } else if (sourceArray === 'otherDishTitles') {
    dishesStore.value.otherDishTitles.splice(index, 1);
  } else {
    dishesStore.value.otherDishTitlesFromIngredients.splice(index, 1);
  }
  if (generationParams.value.dish_title) {
    lastGenerationParams.value.dish_title = generationParams.value.dish_title;
  }
  const { data, error } = await fetchData(useEnvStore().apiUrl + '/generate/dish', 'POST', lastGenerationParams.value);

  if (error || !data.response) {
    const tempDishIndex = dishesStore.value.dishes.findIndex((dish) => dish.id === tempId);
    if (tempDishIndex !== -1) {
      dishesStore.value.dishes.splice(tempDishIndex, 1);
    }
    if (sourceArray === 'dishTitles') {
      dishesStore.value.dishTitles.splice(index, 0, dishTitle);
    } else {
      dishesStore.value.otherDishTitles.splice(index, 0, dishTitle);
    }
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('creationFilters.generationError'),
      life: 3000,
    });
    console.log('error', error);
  } else {
    const newDishIndex = dishesStore.value.dishes.findIndex((d) => d.id === placeholderDish.id);
    if (newDishIndex !== -1) {
      dishesStore.value.dishes[newDishIndex] = {
        ...data.response,
      };
      // Sélectionne le plat généré
      useDishesStore().selectedDish = dishesStore.value.dishes[newDishIndex];
      console.log('[Onboarding] Plat sélectionné après génération:', useDishesStore().selectedDish);
      // Démarre le polling pour onboarding summary
      let summaryEmitted = false;
      const pollInterval = 100;
      const maxTries = 50;
      let tries = 0;
      const pollForSummaryCard = () => {
        const card = document.querySelector('.onboarding-recipe-summary');
        if (card && !summaryEmitted) {
          eventBus.emit('onboarding:recipe-summary-ready');
          summaryEmitted = true;
          console.log('[Onboarding] Event onboarding:recipe-summary-ready emitted (any summary card present)', card);
        } else if (!summaryEmitted && tries < maxTries) {
          tries++;
          setTimeout(pollForSummaryCard, pollInterval);
        } else if (!summaryEmitted) {
          console.warn('[Onboarding] .onboarding-recipe-summary non trouvée après polling.');
        }
      };
      pollForSummaryCard();
    }
    // Remove the temporary placeholder dish
    const tempDishIndex = dishesStore.value.dishes.findIndex((dish) => dish.id === tempId);
    if (tempDishIndex !== -1) {
      dishesStore.value.dishes.splice(tempDishIndex, 1);
    }
  }
  generatingDish.value = false;
}

const confirmGenerateNewDish = (event, dishTitle, index, sourceArray) => {
  selectedDishTitle.value = dishTitle;

  confirm.require({
    group: 'templating',
    header: selectedDishTitle.value,
    // message: t('menuDishView.generateDishFromTitle.popupLabel', { dishTitle }),
    message: '',
    icon: 'pi pi-question-circle',
    acceptLabel: t('menuDishView.generateDishFromTitle.confirm'),
    rejectLabel: t('menuDishView.generateDishFromTitle.cancel'),
    accept: () => {
      generateDishFromTitle(dishTitle, index, sourceArray);
      toast.add({
        severity: 'info',
        summary: t('common.loading'),
        detail: t('menuDishView.loadingMenuLabel'),
        life: 3000,
      });
    },
    reject: () => {
      console.log('reject');
      if (onboarding.isOnboardingActive.value) {
        eventBus.emit('onboarding:dialog-canceled-by-user-during-dish-generation');
      }
    },
  });
};

function planDishTitle(rejectCallback) {
  if (!selectedDishTitle.value) return;

  if (planningStore.choosingDishToPlan) {
    emit('selectTitle', selectedDishTitle.value);
    rejectCallback();
  } else {
    planningStore.dishTitleToPlan = {
      clipboardDishTitle: selectedDishTitle.value,
      selectedPlannedMenuIds: [],
      dishType: 'main',
    };
    rejectCallback();
    showAddToPlanningDialog.value = true;
  }
}

// function handleTitleClick(event, dishTitle: string, index: number, sourceArray: string) {
//   if (planningStore.choosingDishToPlan) {
//     emit('selectTitle', dishTitle)
//   } else {
//     confirmGenerateNewDish(event, dishTitle, index, sourceArray)
//   }
// }

onMounted(() => {
  if (!dishesStore.value.dishTitles.length && !dishesStore.value.isGenerating && !dishesStore.value.dishes.length) {
    router.go(-1);
  }

  // Émet un événement mitt dès qu'un élément .recipe-item est présent dans le DOM
  watch(
    () => dishesStore.value.dishTitles.length,
    async (newLength) => {
      if (newLength > 0) {
        await nextTick();
        const recipeItem = document.querySelector('.recipe-item');
        if (recipeItem) {
          eventBus.emit('onboarding:recipe-list-ready');
          console.log('[Onboarding] Event onboarding:recipe-list-ready émis (recipe-item présent)');
        }
      }
    },
    { immediate: true },
  );
  eventBus.on('onboarding:dialog-close-dishes-validation', () => {
    console.log('[Onboarding] Fermeture du ConfirmDialog via event bus');
    confirm.close();
  });
});

onUnmounted(() => {
  dishesStore.value.resetGenerationParams();
});
</script>

<template>
  <main class="flex flex-col gap-4">
    <LoadingBar v-if="dishesStore.isGenerating && dishesStore.dishes.length <= 1 && !dishesStore.dishTitles.length" :duration-in-seconds="18" />

    <!-- <ProgressBar :value="50"></ProgressBar> -->
    <GenerationResultCard
      v-for="dish in dishesStore.dishes"
      :id="dish.id"
      :key="dish.id"
      :dish="dish"
      :onboarding-summary="dish.id === useDishesStore().selectedDish?.id"
      :class="['generation-result-card my-7', dish.id === useDishesStore().selectedDish?.id ? 'onboarding-recipe-summary' : '']"
      @click="dish.loading ? null : dishClicked(dish)"
    />

    <div v-if="dishesStore.dishTitles" id="dishesListResult" class="mt-5">
      <h1 v-if="dishesStore.dishes.length">
        {{
          dishesStore.dishes.length > 0
            ? t('dishGeneration.dishTitles.suggestions.otherSuggestions')
            : t('dishGeneration.dishTitles.suggestions.suggestions')
        }}
      </h1>
      <div v-if="!dishesStore.dishTitles.length && dishesStore.isGenerating" class="mt-5">
        <TitleSuggestionButton v-for="index in 5" :key="index" />
      </div>
      <TitleSuggestionButton
        v-for="(dishTitle, index) in dishesStore.dishTitles.slice(0, titlesToShow)"
        :key="index"
        :title="dishTitle"
        @click="confirmGenerateNewDish($event, dishTitle, index, 'dishTitles')"
      />
      <p v-if="titlesToShow < dishesStore.dishTitles.length" class="inline-button mb-10" @click="showMore">
        {{ t('common.showMore') }}
      </p>
    </div>

    <!-- <div class="mt-5" v-if="dishesStore.otherDishTitles.length > 0">
      <h1 v-if="dishesStore.dishes">{{ t('dishGeneration.dishTitles.suggestions.accordingToCountry') }}</h1>
      <TitleSuggestionButton :title="otherDishTitle"
        @click="confirmGenerateNewDish($event, otherDishTitle, index, 'otherDishTitles')"
        v-for="(otherDishTitle, index) in dishesStore.otherDishTitles.slice(0, titlesToShow)" :key="index" />
      <p v-if="titlesToShow < dishesStore.otherDishTitles.length" @click="showMore" class="mb-10 inline-button">
        {{ t('common.showMore') }}
      </p>
    </div>

    <div class="mt-5" v-if="dishesStore.otherDishTitlesFromIngredients.length > 0">
      <h1 v-if="dishesStore.dishes">{{ t('dishGeneration.dishTitles.suggestions.accordingToIngredients') }}</h1>
      <TitleSuggestionButton :title="otherDishTitle" @click="
          confirmGenerateNewDish($event, otherDishTitle, index, 'otherDishTitlesFromIngredients')
        " v-for="(otherDishTitle, index) in dishesStore.otherDishTitlesFromIngredients.slice(0, titlesToShow)"
        :key="index" />
      <p v-if="titlesToShow < dishesStore.otherDishTitlesFromIngredients.length" @click="showMore"
        class="mb-10 inline-button">
        {{ t('common.showMore') }}
      </p>
    </div> -->

    <ConfirmDialog id="confirm-recipe-selection-dialog" group="templating">
      <template #container="{ message, acceptCallback, rejectCallback }">
        <div class="flex flex-col rounded bg-white p-5">
          <button class="absolute right-1 top-1 p-2 text-gray-600 hover:text-gray-900" @click="rejectCallback">
            <i class="pi pi-times"></i>
          </button>
          <span class="mb-2 mt-2 block font-bold text-xl">{{ message.header }}</span>
          <p class="mb-4">{{ message.message }}</p>
          <div class="flex justify-end gap-2">
            <!--<Button :label="message.rejectLabel" outlined @click="rejectCallback"></Button>-->
            <div class="flex justify-end gap-2">
              <Button
                :label="t('menuDishView.generateDishFromTitle.planDishTitle')"
                severity="secondary"
                :disabled="onboarding.isOnboardingActive.value"
                @click="planDishTitle(rejectCallback)"
              ></Button>
              <Button id="confirm-recipe-btn" :label="message.acceptLabel" @click="acceptCallback"></Button>
            </div>
          </div>
        </div>
      </template>
    </ConfirmDialog>

    <!-- <GenerationInfos v-if="dishesStore.infoTotal && useEnvStore().environment !== 'production'" :info-total="dishesStore.infoTotal" /> -->
    <AddDishTitleToPlanningDialog v-model:visible="showAddToPlanningDialog" @close-sidebar="showAddToPlanningDialog = false" />
  </main>
</template>
